import { Redirect, useLocalSearchParams } from 'expo-router';
import { useEffect, useRef } from 'react';

import PublicUserProfile from '@/components/publicUserProfile';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { segmentClient } from '@/util/analytics';

/**
 * The `source` values that mean "we sent this person here about their own
 * work", and so may be answered with a sign-in.
 *
 * A named list rather than "any source at all". The parameter is a general
 * attribution tag — the nav bar, the chat header and the account tab all set
 * one — so gating on its mere presence would turn the next tagged profile
 * link into a login wall, silently, for whoever added the tag. The failure
 * would show up as a public link that stopped being public.
 *
 * Deliberately one entry. Push deep links to a profile carry
 * `push_notification` and arguably belong here for the same reason, but
 * nothing has verified where those land or who receives them, and a login
 * wall added on reasoning alone is the thing this list exists to prevent.
 * Adding an entry is a line, once there is evidence for it.
 */
const SIGN_IN_ON_ARRIVAL_SOURCES = new Set(['publication_notification']);

/** Rebuilds the query string from the params the route was reached with. */
function toQueryString(params: Record<string, string | string[] | undefined>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string') {
      search.append(key, value);
    } else if (Array.isArray(value)) {
      for (const entry of value) {
        search.append(key, entry);
      }
    }
  }

  return search.toString();
}

/**
 * The public face of a profile, and the two hand-offs out of it.
 *
 * Both route groups declare `/profile/[id]` and this is the one that matches,
 * so every arrival lands here and is sorted by who is asking.
 *
 * **A signed-in viewer is sent on with every parameter intact.** A publication
 * congratulation links to the publications section with a source tag and the
 * click-tracking pair, and a redirect carrying only the id dropped all four:
 * the expert landed at the top of their profile rather than the paper they
 * were being congratulated on, and the profile-viewed event never fired, since
 * it fires only when a source is present. The arrival was wrong and invisible
 * at once. The secure route's own self-referring redirect has spread the rest
 * of its params for this reason since it was written.
 *
 * **A signed-out viewer who arrived from a notification is sent to sign in and
 * returned here afterwards.** The slim public profile below is built for
 * someone browsing the directory: it stops at About Me, and the publications
 * list is not on it, because that list is authenticated at the API. So an
 * expert tapping their own congratulation while signed out reached a page that
 * could not show them the thing it congratulated them on. Signing in is the
 * only route to it, and it is a reasonable ask of someone opening a message
 * about their own work.
 *
 * **Only the arrivals named below are sent to sign in.** Browsing to a profile
 * from the directory still renders the public page, because putting a login
 * wall in front of public browsing would break the thing that page exists
 * for — and the source parameter alone does not distinguish the two, since
 * plenty of in-app navigation sets one.
 */
export default function PublicProfileRoute() {
  const { id, ...rest } = useLocalSearchParams<{ id: string; source?: string }>();

  const { isAuthenticated, isLoading } = useAuthStatus();

  const { getLoginPathWithRedirect } = useRedirectPath();

  const source = typeof rest.source === 'string' ? rest.source : undefined;

  const needsSignIn =
    !isLoading &&
    !isAuthenticated &&
    source !== undefined &&
    SIGN_IN_ON_ARRIVAL_SOURCES.has(source);

  /**
   * Reported because nothing else can see this happen.
   *
   * The signed-in path is instrumented by the profile screen itself, which
   * this viewer never reaches, so a signed-out arrival from a notification
   * produces no event anywhere — the one population worth counting is the one
   * that was invisible. Guarded by a ref because the redirect below unmounts
   * this screen, and a remount would otherwise count the same arrival twice.
   */
  const reported = useRef(false);

  useEffect(() => {
    if (!needsSignIn || reported.current) {
      return;
    }

    reported.current = true;

    Promise.resolve(
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_SIGN_IN_REQUIRED, {
        source,
        expert_id: id,
      })
    ).catch(() => undefined);
  }, [needsSignIn, source, id]);

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return (
      <Redirect
        href={{ pathname: '/(secure)/(tabs)/(directory)/profile/[id]', params: { ...rest, id } }}
      />
    );
  }

  if (needsSignIn) {
    const query = toQueryString(rest);

    return (
      <Redirect href={getLoginPathWithRedirect(`/profile/${id}${query ? `?${query}` : ''}`)} />
    );
  }

  return <PublicUserProfile />;
}
